import type { FinalCheckOrder, InFlightOrder, LookupableOrder, ReadyToSendOrder } from '../types'
import { shownInFlight } from './in-flight'

// R572 (b1), the till's two faces (Rook #69053, Dee 11-05): the Orders face is ONE list built
// from the till's four existing reads, each order once, at its stage, with one action. The
// ringing orders come first (the ring's read carries order ids), and only today's orders, the
// Beirut day, are listed (Dee via Rook #69065).

export type OrderStage = 'inFlight' | 'finalCheck' | 'pickup' | 'sendOut'

export type OrderEntry =
  | { stage: 'inFlight'; id: string; ringing: boolean; order: InFlightOrder }
  | { stage: 'finalCheck'; id: string; ringing: boolean; order: FinalCheckOrder }
  | { stage: 'pickup'; id: string; ringing: boolean; order: LookupableOrder }
  | { stage: 'sendOut'; id: string; ringing: boolean; order: ReadyToSendOrder }

export interface OrderSources {
  inFlight: InFlightOrder[] | null
  finalCheck: FinalCheckOrder[] | null
  pickup: LookupableOrder[] | null
  sendOut: ReadyToSendOrder[] | null
}

/** The till's day is Beirut's (the ring's quiet hours read the same zone). */
export const TILL_TIME_ZONE = 'Asia/Beirut'

/** The calendar day of a moment in Beirut, as YYYY-MM-DD. */
export function tillDay(at: Date): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: TILL_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).format(at)
}

/**
 * The one "today" rule of the Orders list: a moment is today when it falls on the same Beirut
 * calendar day as now. An unreadable moment is not today. `null` means the read carries no
 * moment at all (the ready-for-pickup read before (b0)): such an order is listed.
 */
export function isTillToday(at: string | null, now: Date): boolean {
  if (at === null) return true
  const when = new Date(at)
  if (Number.isNaN(when.getTime())) return false
  return tillDay(when) === tillDay(now)
}

/**
 * R572 (b0) adds placedAt to the ready-for-pickup and ready-to-send reads (Rook #69101), so the
 * till's today is the order's placedAt on every card. Until (b0) is on the server those reads
 * carry none: a pickup order is then listed, and a ready-to-send order falls back to readyAt.
 */
type Placed = { placedAt?: string | null }

/** The moment each card is bounded by: the order's placedAt. */
export function stageMoment(entry: OrderEntry): string | null {
  switch (entry.stage) {
    case 'inFlight':
      return entry.order.placedAt
    case 'finalCheck':
      return entry.order.placedAt
    case 'sendOut':
      return (entry.order as ReadyToSendOrder & Placed).placedAt ?? entry.order.readyAt
    case 'pickup':
      return (entry.order as LookupableOrder & Placed).placedAt ?? null
  }
}

/**
 * Assembles the Orders list: the four reads in the till's own order (in flight, final check,
 * ready for pickup, ready to send), each order once (the first stage that lists it wins), only
 * today's, and the ringing ones first in the ring's own order.
 */
export function buildOrderList(sources: OrderSources, ringIds: readonly string[], now: Date): OrderEntry[] {
  const ringing = new Set(ringIds)
  const all: OrderEntry[] = [
    ...(sources.inFlight ?? []).filter(shownInFlight).map((order) => ({ stage: 'inFlight' as const, id: order.id, ringing: ringing.has(order.id), order })),
    ...(sources.finalCheck ?? []).map((order) => ({ stage: 'finalCheck' as const, id: order.id, ringing: ringing.has(order.id), order })),
    ...(sources.pickup ?? []).map((order) => ({ stage: 'pickup' as const, id: order.id, ringing: ringing.has(order.id), order })),
    ...(sources.sendOut ?? []).map((order) => ({ stage: 'sendOut' as const, id: order.id, ringing: ringing.has(order.id), order })),
  ]
  const seen = new Set<string>()
  const listed: OrderEntry[] = []
  for (const entry of all) {
    if (seen.has(entry.id)) continue
    seen.add(entry.id)
    if (!isTillToday(stageMoment(entry), now)) continue
    listed.push(entry)
  }
  const ringOrder = (id: string) => ringIds.indexOf(id)
  const first = listed.filter((e) => e.ringing).sort((a, b) => ringOrder(a.id) - ringOrder(b.id))
  const rest = listed.filter((e) => !e.ringing)
  return [...first, ...rest]
}
